import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { IntlProvider, addLocaleData } from "react-intl";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import { bindActionCreators, compose } from "redux";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale-provider/en_US";
import * as types from "./constants";
import CapSpin from "../CapSpin";

import * as selectors from "./selector";
import * as action from "./actions";

const { makeSelectLanguage, makeSelectSupportedLocalesDetails } = selectors;

/**
 * lang could either be user.iso_lang from auth/user call (or) locale from local storage. lang consists of 4 english chars like - en_US or fr-FR
 * For antd(4 chars) - pattern is in `_`. like en_US, fr_FR. So if lang is in en-US we need to convert it to en_US antd
 * For react intl(2 chars), languages are defined using two chars only. i.e. 'en' 'fr' etc. so en_US -> en and fr-FR -> fr. This handles both cases where there might be underscore/hypen
 * @returns antdLocale(for antd), reactIntlLocale(for react intl) and locale(locize call to be made for translations))
 */
function getAllLocales({ lang, supportedLocales }) {
  let antdLocale = lang?.replace("-", "_");
  let reactIntlLocale = lang?.split("-")?.[0]?.split("_")?.[0];
  let locale = supportedLocales?.find(data => data.locale === lang)
    ?.locize_locale;

  return { antdLocale, reactIntlLocale, locale };
}

export class CapLanguageProvider extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      locale: enUS,
      userLoaded: false,
      supportedLocaleLoaded: false,
      localeLoaded: false
    };
  }

  componentDidUpdate() {
    const {
      user,
      supportedLocalesDetails: { supportedLocales }
    } = this.props;

    if (
      !isEmpty(user) &&
      !isEmpty(supportedLocales) &&
      !this.state.localeLoaded
    ) {
      this.setState({ localeLoaded: true });
      this.updateLocale();
    }
  }

  checkLoginPage(){
    return window.location.pathname.indexOf("/login") !== -1;
  }

  componentDidMount() {
    this.props.actions.getSupportedLocales({
      supportedLocalesApi: this.props.supportedLocalesApi,
      isSkip: this.checkLoginPage(),
    });
  }

  updateLocale() {
    const { supportedLocales = [] } = this.props.supportedLocalesDetails || {};
    /**
     * Admin user locale's first locale. Otherwise if org_locale exists -> first org_locale else default_cluster_locale or (en_US);
     */
    const userLocale = localStorage.getItem("locale");
    /**
     * Initialize with default values.
     */
    let locale = "en-US";
    let antdLocale = "en_US";
    let reactIntlLocale = "en";

    let userData = localStorage.getItem("user");
    userData = !isEmpty(userData) ? JSON.parse(userData) : {};
    if (!isEmpty(userData.iso_lang) && !isEmpty(supportedLocales)) {
      let {
        locale: newLocale,
        antdLocale: newAntdLocale,
        reactIntlLocale: newReactIntlLocale
      } = getAllLocales({ lang: userData.iso_lang, supportedLocales });
      locale = newLocale;
      antdLocale = newAntdLocale;
      reactIntlLocale = newReactIntlLocale;
    } else if (!isEmpty(userLocale) && !isEmpty(supportedLocales)) {
      let {
        locale: newLocale,
        antdLocale: newAntdLocale,
        reactIntlLocale: newReactIntlLocale
      } = getAllLocales({ lang: userLocale, supportedLocales });
      locale = newLocale;
      antdLocale = newAntdLocale;
      reactIntlLocale = newReactIntlLocale;
    }
    console.log("Using locale", locale);
    this.props.actions.getLocizeMessage({
      locale,
      isSkip: this.checkLoginPage(),
      locizeApi: this.props.locizeApi
    });
    moment.locale(locale);

    /**In case of any errors, use default as fallback. */
    import(`antd/es/locale-provider/${antdLocale}.js`)
      .then(module => {
        this.setState({ locale: module.default });
      })
      .catch(() => {
        console.warn(
          `antd es locale-provider module does not exist for ${antdLocale}. Using en_US instead`
        );
        import(`antd/es/locale-provider/en_US.js`).then(module => {
          this.setState({ locale: module.default });
        });
      });

    /**In case of any errors, use default as fallback. */
    import(`react-intl/locale-data/${reactIntlLocale}.js`)
      .then(module => {
        addLocaleData(module.default);
      })
      .catch(() => {
        console.warn(
          `react-intl locale-data does not exist for ${reactIntlLocale}. Using en instead`
        );
        import(`react-intl/locale-data/en.js`).then(module => {
          addLocaleData(module.default);
        });
      });
  }
  // eslint-disable-line react/prefer-stateless-function
  render() {
    const { language, messages, supportedLocaleDetails = {},spinnerVisible = true } = this.props;
    const translatedMessages = language.messages || messages;
    const isLoginPage = this.checkLoginPage();
    const showSpinner = spinnerVisible &&
      !isLoginPage &&
      (language.localeLoading === types.REQUEST ||
        supportedLocaleDetails?.fetchSupportedLocalesStatus === types.REQUEST);
    return (
      <ConfigProvider locale={this.state.locale}>
        <IntlProvider
          locale={language.locale}
          key={language.locale}
          messages={translatedMessages}
          onError={() => {}}
        >
          <CapSpin className={this.props.className} spinning={showSpinner}>
            {React.Children.only(this.props.children)}
          </CapSpin>
        </IntlProvider>
      </ConfigProvider>
    );
  }
}

CapLanguageProvider.propTypes = {
  user: PropTypes.object,
  actions: PropTypes.object,
  children: PropTypes.any,
  language: PropTypes.object,
  messages: PropTypes.object,
  supportedLocalesDetails: PropTypes.object,
  className: PropTypes.string,
  spinnerVisible: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  language: makeSelectLanguage(),
  supportedLocalesDetails: makeSelectSupportedLocalesDetails()
});

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(action, dispatch)
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(CapLanguageProvider);
